import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { fetchAnnonces } from "@/lib/api";
import { CATEGORIES, SUBCATEGORIES, ENERGIES, BOITES, VEHICULE_MARQUES, MOTO_MARQUES, CYLINDREES, isMoto, fmtPrice } from "@/lib/utils";
import { useCountry } from "@/lib/CountryContext";
import AnnonceCard from "@/components/AnnonceCard";
import { Search, ChevronLeft, ChevronRight, Plus } from "lucide-react";

const MAX_PRICE = 200000000;

export default function AnnoncesPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const { code: countryCode, country } = useCountry();

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const subcategory = params.get("subcategory") || "";
  const tag = params.get("tag") || "";
  const city = params.get("city") || "";
  const maxPrice = parseInt(params.get("max_price") || MAX_PRICE, 10);
  const sort = params.get("sort") || "recent";
  const page = parseInt(params.get("page") || "1", 10);
  // Phase 2 Immobilier — pass-through filters (read from URL, sent to API)
  const type_bien = params.get("type_bien") || "";
  const statut = params.get("statut") || "";
  const etat_bien = params.get("etat_bien") || "";
  const documents_terrain = params.get("documents_terrain") || "";
  const usage_terrain = params.get("usage_terrain") || "";
  const energie = params.get("energie") || "";
  const marque = params.get("marque") || "";
  const boite = params.get("boite") || "";
  const cylindree = params.get("cylindree") || "";
  const includeSold = params.get("vendues") === "1";
  const annee_min = params.get("annee_min") || "";
  const km_max = params.get("km_max") || "";
  const pageSize = 12;

  const update = (key, val, resetPage = true) => {
    const next = new URLSearchParams(params);
    if (val === "" || val === null || val === undefined) next.delete(key);
    else next.set(key, val);
    if (resetPage && key !== "page") next.delete("page");
    setParams(next, { replace: true });
  };

  useEffect(() => {
    setLoading(true);
    fetchAnnonces({
      q: q || undefined,
      category: category || undefined,
      subcategory: subcategory || undefined,
      tag: tag || undefined,
      city: city || undefined,
      country: countryCode,
      max_price: maxPrice || undefined,
      type_bien: type_bien || undefined,
      statut: statut || undefined,
      etat_bien: etat_bien || undefined,
      documents_terrain: documents_terrain || undefined,
      usage_terrain: usage_terrain || undefined,
      energie: energie || undefined,
      marque: marque || undefined,
      boite: boite || undefined,
      cylindree: cylindree || undefined,
      include_sold: includeSold || undefined,
      annee_min: annee_min || undefined,
      km_max: km_max || undefined,
      sort,
      page,
      page_size: pageSize,
    })
      .then(setData)
      .finally(() => setLoading(false));
  }, [q, category, subcategory, tag, city, countryCode, maxPrice, type_bien, statut, etat_bien, documents_terrain, usage_terrain, energie, marque, boite, cylindree, includeSold, annee_min, km_max, sort, page]);

  const setCategory = (cat) => {
    const next = new URLSearchParams(params);
    if (cat) next.set("category", cat); else next.delete("category");
    next.delete("subcategory");
    next.delete("page");
    setParams(next, { replace: true });
  };

  const resetFilters = () => setParams({}, { replace: true });
  // Keep category/type_bien/statut, drop the fine-grained sub-filters (documents, usage, etat, subcategory…).
  const hasSubFilters = !!(documents_terrain || usage_terrain || etat_bien || subcategory || energie || marque || boite || cylindree || annee_min || km_max || maxPrice < MAX_PRICE || q);
  const widenSearch = () => {
    const keep = {};
    if (category) keep.category = category;
    if (type_bien) keep.type_bien = type_bien;
    if (statut) keep.statut = statut;
    setParams(keep, { replace: true });
  };
  const goTo = (p) => update("page", String(p), false);

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="annonces-page">
      <div className="mb-8">
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Toutes les annonces</h1>
        <p className="text-[var(--ink-soft)] mt-2">Filtres riches, favoris persistants et lecture premium.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
        <aside className="glass-card p-6 h-fit lg:sticky lg:top-24" data-testid="filters-sidebar">
          <div className="flex items-center justify-between mb-4">
            <div className="font-bold text-lg">Filtres</div>
            <button onClick={resetFilters} className="text-xs text-orange-600 font-semibold" data-testid="reset-filters-btn">Reinitialiser</button>
          </div>

          <div className="mb-5">
            <label>Recherche</label>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-3.5 text-[var(--ink-soft)]" />
              <input type="text" value={q} onChange={(e) => update("q", e.target.value)} placeholder="Villa, voiture, FD-000123..." className="pl-9" data-testid="filter-search-input" />
            </div>
          </div>

          <div className="mb-5">
            <label>Categorie</label>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setCategory("")} className={`chip ${!category ? "active" : ""}`} data-testid="filter-cat-toutes">Toutes</button>
              {CATEGORIES.map((c) => (
                <button key={c.key} onClick={() => setCategory(c.key)} className={`chip ${category === c.key ? "active" : ""}`} data-testid={`filter-cat-${c.key}`}>{c.key}</button>
              ))}
            </div>
          </div>

          {SUBCATEGORIES[category] && (
            <div className="mb-5" data-testid="filter-subcategories">
              <label>Sous-categorie</label>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => update("subcategory", "")} className={`chip ${!subcategory ? "active" : ""}`} data-testid="filter-subcat-toutes">Toutes</button>
                {Object.values(SUBCATEGORIES[category]).flat().filter((v, i, a) => a.indexOf(v) === i).map((s) => (
                  <button key={s} onClick={() => update("subcategory", s)} className={`chip ${subcategory === s ? "active" : ""}`} data-testid={`filter-subcat-${s}`}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {category === "Vehicules" && (
            <div className="mb-5 p-3 rounded-2xl bg-orange-50/50 border border-orange-100 space-y-3" data-testid="filter-vehicule">
              <div data-testid="filter-marque">
                <label>Marque</label>
                <select value={marque} onChange={(e) => update("marque", e.target.value)} data-testid="filter-marque-select">
                  <option value="">Toutes les marques</option>
                  {(isMoto(subcategory) ? MOTO_MARQUES : VEHICULE_MARQUES).map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div data-testid="filter-energie">
                <label>Energie</label>
                <select value={energie} onChange={(e) => update("energie", e.target.value)} data-testid="filter-energie-select">
                  <option value="">Toutes les energies</option>
                  {ENERGIES.map((en) => <option key={en.key} value={en.key}>{en.label}</option>)}
                </select>
              </div>
              <div data-testid="filter-boite">
                <label>Boite</label>
                <select value={boite} onChange={(e) => update("boite", e.target.value)} data-testid="filter-boite-select">
                  <option value="">Toutes</option>
                  {BOITES.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
                </select>
              </div>
              {isMoto(subcategory) && (
                <div data-testid="filter-cylindree">
                  <label>Cylindree</label>
                  <select value={cylindree} onChange={(e) => update("cylindree", e.target.value)} data-testid="filter-cylindree-select">
                    <option value="">Toutes</option>
                    {CYLINDREES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label>Annee min</label>
                  <input type="number" inputMode="numeric" min="1980" value={annee_min} onChange={(e) => update("annee_min", e.target.value)} placeholder="2015" data-testid="filter-annee-min" />
                </div>
                <div>
                  <label>Km max</label>
                  <input type="number" inputMode="numeric" min="0" step="10000" value={km_max} onChange={(e) => update("km_max", e.target.value)} placeholder="150000" data-testid="filter-km-max" />
                </div>
              </div>
            </div>
          )}

          <div className="mb-5">
            <label>Ville</label>
            <select value={city} onChange={(e) => update("city", e.target.value)} data-testid="filter-city-select">
              <option value="">Toutes les villes</option>
              {country.cities.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>

          <div className="mb-5">
            <label>Prix max : {fmtPrice(maxPrice)}</label>
            <input type="range" min={50000} max={MAX_PRICE} step={50000} value={maxPrice} onChange={(e) => update("max_price", e.target.value)} data-testid="filter-price-range" />
          </div>

          <label className="mb-3 flex items-center gap-2 text-sm font-semibold cursor-pointer select-none" data-testid="filter-show-sold">
            <input type="checkbox" checked={includeSold} onChange={(e) => update("vendues", e.target.checked ? "1" : "")} className="accent-emerald-600 w-4 h-4" data-testid="filter-show-sold-input" />
            Voir les annonces vendues
          </label>
          <div className="mb-2">
            <label>Trier par</label>
            <select value={sort} onChange={(e) => update("sort", e.target.value)} data-testid="filter-sort-select">
              <option value="recent">Plus recentes</option>
              <option value="price_asc">Prix croissant</option>
              <option value="price_desc">Prix decroissant</option>
            </select>
          </div>
        </aside>

        <section>
          <div className="flex items-center justify-between mb-5">
            <div className="font-bold" data-testid="annonces-count">
              {loading ? "Chargement..." : `${data.total} annonce${data.total > 1 ? "s" : ""}`}
            </div>
            {data.total_pages > 1 && (
              <div className="text-sm text-[var(--ink-soft)]">Page <b>{data.page}</b> / {data.total_pages}</div>
            )}
          </div>

          {!loading && data.items.length === 0 ? (
            <div className="glass-card p-10 text-center" data-testid="empty-state">
              <div className="text-lg font-bold mb-2">Aucune annonce ne correspond a vos filtres</div>
              <p className="text-sm text-[var(--ink-soft)] mb-1">
                Soyez le premier ! Deposez votre annonce sur cette categorie et apparaissez en tete des resultats.
              </p>
              <p className="text-xs text-[var(--ink-soft)]">
                Vous pouvez aussi elargir votre recherche ou reinitialiser les filtres.
              </p>
              <div className="mt-5 flex flex-wrap gap-2 justify-center">
                <Link to="/deposer" className="btn-primary inline-flex items-center gap-2" data-testid="empty-state-deposer-btn">
                  <Plus size={14} /> Deposer cette annonce
                </Link>
                {hasSubFilters && (
                  <button onClick={widenSearch} className="btn-secondary" data-testid="empty-state-widen-btn">
                    Elargir la recherche{type_bien ? ` (tous les ${type_bien}s)` : ""}
                  </button>
                )}
                <button onClick={resetFilters} className="btn-secondary" data-testid="empty-state-reset-btn">
                  Reinitialiser les filtres
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {data.items.map((a, i) => (
                  <div key={a.id} className="rise" style={{ animationDelay: `${i * 0.03}s` }}>
                    <AnnonceCard a={a} />
                  </div>
                ))}
              </div>

              {data.total_pages > 1 && (
                <Pagination page={data.page} totalPages={data.total_pages} onGo={goTo} />
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}

function Pagination({ page, totalPages, onGo }) {
  const pages = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, start + 4);
  for (let p = start; p <= end; p++) pages.push(p);

  return (
    <div className="mt-8 flex items-center justify-center gap-2" data-testid="pagination">
      <button onClick={() => onGo(page - 1)} disabled={page === 1} className="btn-secondary !px-3 disabled:opacity-40" data-testid="page-prev">
        <ChevronLeft size={16} />
      </button>
      {start > 1 && (
        <>
          <button onClick={() => onGo(1)} className="btn-secondary !px-3" data-testid="page-1">1</button>
          {start > 2 && <span className="text-[var(--ink-soft)]">...</span>}
        </>
      )}
      {pages.map((p) => (
        <button
          key={p}
          onClick={() => onGo(p)}
          className={`btn-secondary !px-3 ${p === page ? "!bg-gradient-to-br !from-orange-500 !to-orange-400 !text-white !border-transparent" : ""}`}
          data-testid={`page-${p}`}
        >
          {p}
        </button>
      ))}
      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span className="text-[var(--ink-soft)]">...</span>}
          <button onClick={() => onGo(totalPages)} className="btn-secondary !px-3" data-testid={`page-${totalPages}`}>{totalPages}</button>
        </>
      )}
      <button onClick={() => onGo(page + 1)} disabled={page === totalPages} className="btn-secondary !px-3 disabled:opacity-40" data-testid="page-next">
        <ChevronRight size={16} />
      </button>
    </div>
  );
}
